import React, { useEffect, useRef, useState } from "react";
import { Contacto, DatosEnvio, Dir, MiBuzon, errorCorreo, mailApi, tamano } from "./wappmail.service";

/** Ventana de redactar de WappMail: Para/Cc/Cco con sugerencias del personal de WAPP, editor sencillo, adjuntos y firma. */

export interface Borrador {
	clave: number; para: Dir[]; cc: Dir[]; cco: Dir[]; asunto: string; html: string;
	responder?: { carpeta: string; uid: number }; reenviar?: { carpeta: string; uid: number };
	/** Texto que explica qué se incluye abajo (el original), solo informativo. */
	nota?: string;
}

const MAX_ADJUNTOS = 15 * 1024 * 1024;   // ZeptoMail acepta ~15 MB por correo (wa-mail MAX_ADJUNTOS_MB)
const CORREO = /^[^\s@<>(),;:"]+@[^\s@<>(),;:"]+\.[a-z]{2,}$/i;

const Destinatarios: React.FC<{ etiqueta: string; valor: Dir[]; onChange: (v: Dir[]) => void; autoFoco?: boolean }> = ({ etiqueta, valor, onChange, autoFoco }) => {
	const [texto, setTexto] = useState("");
	const [sug, setSug] = useState<Contacto[]>([]);
	const [pos, setPos] = useState(0);
	const [abierto, setAbierto] = useState(false);
	const ref = useRef<HTMLInputElement>(null);

	useEffect(() => {
		const q = texto.trim();
		if (q.length < 2) { setSug([]); return; }
		let vigente = true;
		const t = setTimeout(() => {
			mailApi.contactos(q).then((r) => {
				if (!vigente) return;
				setSug((r.data.contactos || []).filter((c) => !valor.some((v) => v.email === c.email.toLowerCase())));
				setPos(0);
			}).catch(() => vigente && setSug([]));
		}, 180);
		return () => { vigente = false; clearTimeout(t); };
	}, [texto]); // eslint-disable-line react-hooks/exhaustive-deps

	const agregar = (d: Dir) => {
		const email = d.email.trim().toLowerCase();
		if (!CORREO.test(email) || valor.some((v) => v.email === email)) return;
		onChange([...valor, { nombre: d.nombre, email }]);
	};
	/** Lo escrito (puede venir pegado con varias direcciones). Devuelve true si no quedó nada sin entender. */
	const soltarTexto = (): boolean => {
		const partes = texto.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
		let resto = "";
		const nuevos: Dir[] = [];
		partes.forEach((p) => {
			const e = p.replace(/^<|>$/g, "").toLowerCase();
			if (CORREO.test(e)) { if (!valor.some((v) => v.email === e) && !nuevos.some((v) => v.email === e)) nuevos.push({ nombre: "", email: e }); } else resto += (resto ? " " : "") + p;
		});
		if (nuevos.length) onChange([...valor, ...nuevos]);
		setTexto(resto);
		return !resto;
	};
	const elegir = (c: Contacto) => { agregar({ nombre: c.nombre, email: c.email }); setTexto(""); setSug([]); ref.current?.focus(); };

	const tecla = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (abierto && sug.length && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
			e.preventDefault();
			setPos((p) => (p + (e.key === "ArrowDown" ? 1 : sug.length - 1)) % sug.length);
		} else if (e.key === "Enter" || e.key === "Tab" || e.key === "," || e.key === ";") {
			if (abierto && sug.length && e.key !== "," && e.key !== ";" && texto.trim().length >= 2 && !CORREO.test(texto.trim())) { e.preventDefault(); elegir(sug[pos]); }
			else if (texto.trim()) { e.preventDefault(); soltarTexto(); }
		} else if (e.key === "Backspace" && !texto && valor.length) {
			onChange(valor.slice(0, -1));
		}
	};

	return (
		<div className="wm-dest" onClick={() => ref.current?.focus()}>
			<span className="wm-dest-et">{etiqueta}</span>
			<div className="wm-dest-caja">
				{valor.map((d) => (
					<span key={d.email} className="wm-chip" title={d.email}>
						{d.nombre || d.email}
						<button type="button" aria-label={"Quitar " + d.email} onClick={(e) => { e.stopPropagation(); onChange(valor.filter((v) => v.email !== d.email)); }}>×</button>
					</span>
				))}
				<input ref={ref} value={texto} autoFocus={autoFoco} aria-label={etiqueta} autoComplete="off" spellCheck={false}
					onChange={(e) => { setTexto(e.target.value); setAbierto(true); }}
					onKeyDown={tecla} onFocus={() => setAbierto(true)}
					onBlur={() => { setTimeout(() => setAbierto(false), 150); if (texto.trim()) soltarTexto(); }}
					onPaste={(e) => { const t = e.clipboardData.getData("text"); if (/[,;\s]/.test(t.trim())) { e.preventDefault(); setTexto((x) => x + t); setTimeout(() => ref.current?.blur(), 0); } }} />
				{abierto && sug.length > 0 ? (
					<ul className="wm-sug" role="listbox">
						{sug.map((c, i) => (
							<li key={c.email} role="option" aria-selected={i === pos} className={i === pos ? "on" : ""} onMouseDown={(e) => { e.preventDefault(); elegir(c); }}>
								<b>{c.nombre || c.email}</b>
								<small>{c.tipo === "lista" ? "Lista · " : ""}{c.email}</small>
							</li>
						))}
					</ul>
				) : null}
			</div>
		</div>
	);
};

const BarraEditor: React.FC = () => {
	const cmd = (c: string, v?: string) => (e: React.MouseEvent) => { e.preventDefault(); document.execCommand(c, false, v); };
	const enlace = (e: React.MouseEvent) => {
		e.preventDefault();
		const u = window.prompt("Dirección del enlace (https://…)");
		if (u && /^https?:\/\//i.test(u.trim())) document.execCommand("createLink", false, u.trim());
	};
	return (
		<div className="wm-barra-ed" role="toolbar" aria-label="Formato">
			<button type="button" onMouseDown={cmd("bold")} title="Negrita"><b>B</b></button>
			<button type="button" onMouseDown={cmd("italic")} title="Cursiva"><i>I</i></button>
			<button type="button" onMouseDown={cmd("underline")} title="Subrayado"><u>U</u></button>
			<button type="button" onMouseDown={cmd("insertUnorderedList")} title="Lista">• Lista</button>
			<button type="button" onMouseDown={cmd("insertOrderedList")} title="Lista numerada">1. Lista</button>
			<button type="button" onMouseDown={enlace} title="Enlace">Enlace</button>
			<button type="button" onMouseDown={cmd("removeFormat")} title="Quitar formato">Limpiar</button>
		</div>
	);
};

const Redactar: React.FC<{
	borrador: Borrador; yo: MiBuzon; onCerrar: () => void; onEnviado: (aviso: string) => void;
}> = ({ borrador, yo, onCerrar, onEnviado }) => {
	const [para, setPara] = useState<Dir[]>(borrador.para);
	const [cc, setCc] = useState<Dir[]>(borrador.cc);
	const [cco, setCco] = useState<Dir[]>(borrador.cco);
	const [verCc, setVerCc] = useState(borrador.cc.length > 0 || borrador.cco.length > 0);
	const [asunto, setAsunto] = useState(borrador.asunto);
	const [archivos, setArchivos] = useState<File[]>([]);
	const [firma, setFirma] = useState(true);
	const [enviando, setEnviando] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [confirmaDescartar, setConfirmaDescartar] = useState(false);
	const [arrastrando, setArrastrando] = useState(false);
	const editor = useRef<HTMLDivElement>(null);
	const archivoRef = useRef<HTMLInputElement>(null);

	useEffect(() => { if (editor.current) editor.current.innerHTML = borrador.html; }, []); // eslint-disable-line react-hooks/exhaustive-deps

	const peso = archivos.reduce((t, a) => t + a.size, 0);
	const textoEditor = () => (editor.current?.innerText || "").trim();
	const tieneCambios = () => {
		const esRespuesta = !!(borrador.responder || borrador.reenviar);
		return !!textoEditor() || archivos.length > 0 || (!esRespuesta && !!(para.length || cc.length || cco.length || asunto.trim()));
	};

	const agregarArchivos = (lista: FileList | File[]) => {
		const nuevos = Array.from(lista);
		if (peso + nuevos.reduce((t, a) => t + a.size, 0) > MAX_ADJUNTOS) { setError("Los adjuntos pasan de 15 MB."); return; }
		setError(null);
		setArchivos((a) => [...a, ...nuevos]);
	};

	const cerrar = () => { if (tieneCambios() && !confirmaDescartar) { setConfirmaDescartar(true); return; } onCerrar(); };

	const enviar = async () => {
		if (enviando) return;
		if (!para.length && !cc.length && !cco.length) { setError("Falta a quién mandarlo."); return; }
		const sinAsunto = !asunto.trim();
		if (sinAsunto && !window.confirm("¿Mandar el correo sin asunto?")) return;
		setEnviando(true); setError(null);
		const datos: DatosEnvio = { para, cc, cco, asunto: asunto.trim(), html: editor.current?.innerHTML || "", firma, responder: borrador.responder, reenviar: borrador.reenviar };
		try {
			const r = await mailApi.enviar(datos, archivos);
			onEnviado(r.data.guardado_en_enviados ? "Correo enviado." : "Correo enviado, pero no se pudo guardar en Enviados.");
		} catch (e) {
			setError(errorCorreo(e));
			setEnviando(false);
		}
	};

	return (
		<div className="wm-redactar-fondo" role="dialog" aria-modal="true" aria-label="Redactar correo">
			<div className={"wm-redactar" + (arrastrando ? " arrastrando" : "")}
				onDragOver={(e) => { e.preventDefault(); setArrastrando(true); }} onDragLeave={() => setArrastrando(false)}
				onDrop={(e) => { e.preventDefault(); setArrastrando(false); if (e.dataTransfer.files?.length) agregarArchivos(e.dataTransfer.files); }}>
				<header>
					<h3>{borrador.responder ? "Responder" : borrador.reenviar ? "Reenviar" : "Correo Nuevo"}</h3>
					<button type="button" className="wm-x" aria-label="Cerrar" onClick={cerrar}>×</button>
				</header>
				<div className="wm-red-cuerpo">
					<div className="wm-de">De: <b>{yo.buzon?.email}</b></div>
					<Destinatarios etiqueta="Para" valor={para} onChange={setPara} autoFoco={!para.length} />
					{verCc ? <>
						<Destinatarios etiqueta="Cc" valor={cc} onChange={setCc} />
						<Destinatarios etiqueta="Cco" valor={cco} onChange={setCco} />
					</> : <button type="button" className="av-link wm-vercc" onClick={() => setVerCc(true)}>Cc / Cco</button>}
					<input className="wm-asunto" placeholder="Asunto" aria-label="Asunto" value={asunto} maxLength={400} onChange={(e) => setAsunto(e.target.value)} />
					<BarraEditor />
					<div ref={editor} className="wm-editor" contentEditable suppressContentEditableWarning role="textbox" aria-multiline="true" aria-label="Mensaje" />
					{borrador.nota ? <div className="wm-nota-orig">{borrador.nota}</div> : null}
					{firma && yo.firma?.html ? <div className="wm-firma" dangerouslySetInnerHTML={{ __html: yo.firma.html }} /> : null}
					{archivos.length ? (
						<ul className="wm-archivos">
							{archivos.map((a, i) => (
								<li key={i}><span title={a.name}>{a.name}</span><small>{tamano(a.size)}</small>
									<button type="button" className="wm-x" aria-label={"Quitar " + a.name} onClick={() => setArchivos((l) => l.filter((_, j) => j !== i))}>×</button></li>
							))}
						</ul>
					) : null}
				</div>
				{error ? <div className="wm-aviso oro" role="alert">{error}</div> : null}
				<footer>
					<button type="button" className="av-btn pri" disabled={enviando} onClick={enviar}>{enviando ? "Enviando…" : "Enviar"}</button>
					<button type="button" className="av-btn" onClick={() => archivoRef.current?.click()}>Adjuntar Archivos</button>
					<input ref={archivoRef} type="file" multiple hidden onChange={(e) => { if (e.target.files) agregarArchivos(e.target.files); e.target.value = ""; }} />
					<label className="av-check"><input type="checkbox" checked={firma} onChange={(e) => setFirma(e.target.checked)} /> Firma</label>
					<span className="wm-espacio" />
					{confirmaDescartar
						? <><span className="av-tenue">¿Descartar?</span><button type="button" className="av-btn" onClick={onCerrar}>Sí, Descartar</button><button type="button" className="av-btn" onClick={() => setConfirmaDescartar(false)}>Seguir</button></>
						: <button type="button" className="av-btn" onClick={cerrar}>Descartar</button>}
				</footer>
			</div>
		</div>
	);
};

export default Redactar;
